import React, { useRef, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';

export const ObjectSelectionImageView = ({
  i18n,
  originalImageUrl,
  originalImageHeight,
  originalImageWidth,
  getMaskValueAtPoint,
  updateHighlightedImage,
  isFetchingSegmentations,
  toggleSelectedMask,
  borderImageSrc,
  workingSelectedImageSrc,
  highlightedImageSrc,
  maskValues,
  logger,
}) => {
  // get image from offscreen canvas
  // draw the canvas in the front
  const imageContainer = useRef(null);
  const [maskIndex, setMaskIndex] = useState(undefined);

  const windowToImgPosition = useCallback((x, y) => {
    if (imageContainer.current) {
      const imageBox = imageContainer.current.getBoundingClientRect();
      const targetWidth = imageBox.width;
      const targetHeight = imageBox.height;
      // calculate the ratio of the original image and the target image for object-fit: contain
      const wr = targetWidth / originalImageWidth;
      const hr = targetHeight / originalImageHeight;
      const ratio = Math.min(wr, hr);
      const width = originalImageWidth * ratio;
      const height = originalImageHeight * ratio;
      const leftToImage = (targetWidth - width) / 2.0;
      const topToImage = (targetHeight - height) / 2.0;
      const { top, left } = imageBox;
      return {
        x: ((x - left) - leftToImage) * (originalImageWidth / width),
        y: ((y - top) - topToImage) * (originalImageHeight / height),
      };
    }
    return { x: -1, y: -1 };
  }, [originalImageHeight, originalImageWidth]);

  const mousemoveHandler = useCallback((e) => {
    const { x, y } = windowToImgPosition(e.clientX, e.clientY);
    const maskValue = getMaskValueAtPoint(x, y, originalImageWidth);
    updateHighlightedImage(maskValue);
  }, [getMaskValueAtPoint, originalImageWidth, updateHighlightedImage, windowToImgPosition]);

  const mouseleaveHandler = useCallback(() => {
    updateHighlightedImage(undefined);
  }, [updateHighlightedImage]);

  const mousedownHandler = useCallback((e) => {
    const { x, y } = windowToImgPosition(e.clientX, e.clientY);
    const maskValue = getMaskValueAtPoint(x, y, originalImageWidth);
    if (maskValue > 0) {
      toggleSelectedMask(maskValue);
      logger.logTrace('Copilot change image background - object selection view - object selection');
    }
  }, [getMaskValueAtPoint, logger, originalImageWidth, toggleSelectedMask, windowToImgPosition]);

  const keyHandler = useCallback((e) => {
    if (maskValues && maskValues.length > 0) {
      if (e.key === 'Tab') {
        if (e.shiftKey === true) {
          if (maskIndex === undefined) {
            setMaskIndex(maskValues.length - 1);
            updateHighlightedImage(maskValues[maskValues.length - 1]);
            e.preventDefault();
          } else if (maskIndex - 1 >= 0) {
            setMaskIndex((maskIndex - 1));
            updateHighlightedImage(maskValues[(maskIndex - 1)]);
            e.preventDefault();
          }
        } else if (e.shiftKey === false) {
          if (maskIndex === undefined) {
            setMaskIndex(0);
            updateHighlightedImage(maskValues[0]);
            e.preventDefault();
          } else if ((maskIndex + 1) < maskValues.length - 1) {
            setMaskIndex((maskIndex + 1));
            updateHighlightedImage(maskValues[(maskIndex + 1)]);
            e.preventDefault();
          }
        }
      } else if (e.key === 'Enter' && maskIndex !== undefined) {
        toggleSelectedMask(maskValues[maskIndex]);
        logger.logTrace('Copilot change image background - object selection view - object selection');
        e.preventDefault();
      }
    }
  }, [logger, maskIndex, maskValues, toggleSelectedMask, updateHighlightedImage]);

  const resetMaskHandler = useCallback(() => {
    setMaskIndex(undefined);
    updateHighlightedImage(undefined);
  }, [updateHighlightedImage]);

  useEffect(() => {
    const imageBox = imageContainer.current;
    if (imageBox) {
      imageBox.addEventListener('mousemove', mousemoveHandler);
      imageBox.addEventListener('mouseleave', mouseleaveHandler);
      imageBox.addEventListener('mousedown', mousedownHandler);
      imageBox.addEventListener('keydown', keyHandler);
      imageBox.addEventListener('blur', resetMaskHandler);
      imageBox.addEventListener('focus', resetMaskHandler);
    }
    return () => {
      if (imageBox) {
        imageBox.removeEventListener('mousemove', mousemoveHandler);
        imageBox.removeEventListener('mouseleave', mouseleaveHandler);
        imageBox.removeEventListener('mousedown', mousedownHandler);
        imageBox.removeEventListener('keydown', keyHandler);
        imageBox.removeEventListener('blur', resetMaskHandler);
        imageBox.addEventListener('focus', resetMaskHandler);
      }
    };
  }, [windowToImgPosition, mousemoveHandler, mouseleaveHandler, mousedownHandler, keyHandler, resetMaskHandler]);

  return (
    <div
      className="image-wrapper"
      ref={imageContainer}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      aria-label={i18n.getString(_TL_('Object Selection Control'))}
    >
      {originalImageUrl && (
        <>
          <img className="original-image" src={originalImageUrl} alt={i18n.getString(_TL_('Original image layer'))} />
          <div className="cover-original-image" />
          {isFetchingSegmentations && (<div className="loading-segmentations-image" />)}
          {!isFetchingSegmentations && (
            <>
              {(borderImageSrc && <img className="border-image" src={borderImageSrc} alt={i18n.getString(_TL_('Border image layer'))} />)}
              {(workingSelectedImageSrc && <img className="selected-image" src={workingSelectedImageSrc} alt={i18n.getString(_TL_('Selected image layer'))} />)}
              {(highlightedImageSrc && <img className="highlighted-image" src={highlightedImageSrc} alt={i18n.getString(_TL_('Highlighted image layer'))} />)}
            </>
          )}
        </>
      )}
    </div>
  );
};


ObjectSelectionImageView.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  originalImageUrl: PropTypes.string.isRequired,
  originalImageHeight: PropTypes.number.isRequired,
  originalImageWidth: PropTypes.number.isRequired,
  getMaskValueAtPoint: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  maskValues: PropTypes.arrayOf(PropTypes.number),
  isFetchingSegmentations: PropTypes.bool.isRequired,
};

ObjectSelectionImageView.defaultProps = {
  maskValues: [],
};
